import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cx } from './cx';

type Variant =
  | 'primary'
  | 'secondary'
  | 'accent'
  | 'outline'
  | 'ghost'
  | 'danger'
  | 'dangerSolid'
  | 'warning';

type Size = 'xs' | 'sm' | 'md' | 'lg';

const variantStyles: Record<Variant, string> = {
  primary:     'bg-cyan-500 text-white hover:bg-cyan-400 focus:ring-cyan-500 shadow-sm shadow-cyan-500/20',
  secondary:   'bg-slate-700 text-white hover:bg-slate-600 border border-slate-600',
  accent:      'bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:from-cyan-400 hover:to-blue-500 shadow-lg shadow-cyan-500/25',
  outline:     'bg-slate-800/50 text-slate-200 border border-slate-700 hover:border-cyan-500/40 hover:bg-slate-800 hover:text-white',
  ghost:       'bg-transparent text-slate-300 hover:text-white hover:bg-slate-700/50',
  danger:      'bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20 hover:text-red-300',
  dangerSolid: 'bg-red-500 text-white hover:bg-red-400 focus:ring-red-500 shadow-sm shadow-red-500/30',
  warning:     'bg-amber-500/10 text-amber-400 border border-amber-500/30 hover:bg-amber-500/20',
};

const sizeStyles: Record<Size, string> = {
  xs: 'px-2 py-1 text-[11px]',
  sm: 'px-2.5 py-1.5 text-xs',
  md: 'px-3.5 py-2 text-sm',
  lg: 'px-4 py-2.5 text-sm',
};

export interface BtnProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

export const Btn = forwardRef<HTMLButtonElement, BtnProps>(function Btn(
  { variant = 'primary', size = 'md', leftIcon, rightIcon, className, children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      {...rest}
      className={cx(
        'inline-flex items-center justify-center gap-2 rounded-lg font-semibold transition-colors',
        'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900',
        'disabled:opacity-40 disabled:cursor-not-allowed',
        variantStyles[variant],
        sizeStyles[size],
        className,
      )}
    >
      {leftIcon}
      {children}
      {rightIcon}
    </button>
  );
});
